"use client";

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  getAssistantOptions,
  updateAssistantOptions,
  type AssistantOptionsState,
} from "./storage";

type BooleanKeys = {
  [K in keyof AssistantOptionsState]: AssistantOptionsState[K] extends boolean
    ? K
    : never;
}[keyof AssistantOptionsState];

type ToggleContextValue<K extends BooleanKeys> = {
  [P in K]: boolean;
} & {
  toggle: () => void;
  setThreadId: (threadId: string | undefined) => void;
};

export function createToggleContext<K extends BooleanKeys>(key: K) {
  const Ctx = createContext<ToggleContextValue<K>>({
    [key]: false,
    toggle: () => {},
    setThreadId: () => {},
  } as unknown as ToggleContextValue<K>);

  function Provider({ children }: { children: ReactNode }) {
    const [value, setValue] = useState(false);
    const threadIdRef = useRef<string | undefined>(undefined);

    const setThreadId = useCallback(
      (id: string | undefined) => {
        threadIdRef.current = id;
        setValue(getAssistantOptions(id)[key]);
      },
      [],
    );

    const toggle = useCallback(() => {
      setValue((prev) => {
        const next = !prev;
        updateAssistantOptions(threadIdRef.current, {
          [key]: next,
        } as Partial<AssistantOptionsState>);
        return next;
      });
    }, []);

    return (
      <Ctx.Provider
        value={
          { [key]: value, toggle, setThreadId } as unknown as ToggleContextValue<K>
        }
      >
        {children}
      </Ctx.Provider>
    );
  }

  function useToggle(): ToggleContextValue<K> {
    return useContext(Ctx);
  }

  return [Provider, useToggle] as const;
}
